import { QRCodeSVG } from 'qrcode.react';
import { isEmpty, isNil, isNotEmpty } from 'rambdax';
import React, { useEffect, useRef, useState } from 'react';

import {
  ApiCommodityType,
  useCreateWalletVerificationRequest,
  useGetAllAsset,
  useGetPortfolio,
  useGetWithdrawalReceiveAmountWallet,
  useIsWithdrawalAllowedWallet,
  useVerifyAddressWalletVerificationConnector,
  useWithdrawalWallet,
  VerifyAddressWithSignedFormResult,
  WalletConnectorQrCodePayload,
  WithdrawalAllowanceStatusType,
} from '@ping/api';
import ArrowLeft from '@ping/assets/Icon/arrowLeft.svg';
import CircleWarningIcon from '@ping/assets/Icon/circle-warning.svg';
import {
  ContactCrypto,
  PortfolioTokenSelect,
  TooltipWithdrawalLimitsInfo,
  WithdrawTierUpgradingStepModal,
} from '@ping/components';
import { t } from '@ping/helpers';
import { isDepositWithdrawAssetListed, isWithdrawNetworkDisabled } from '@ping/helpers/deposit-withdraw-access.helper';
import * as gtm from '@ping/helpers/gtm.helper';
import {
  useAvailablePortfolioTokens,
  useConversionAssetToFiat,
  useDebounce,
  useMobile,
  useToggle,
  useWithdrawalLiquidityHint,
  useWithdrawalLimits,
} from '@ping/hooks';
import { useCorePassFinalApproval } from '@ping/pages/user/business/components/trading-governance/CorePassFinalApproval/useCorePassFinalApproval';
import {
  transferOnProcessingModalRemoteStateSelector,
  useModalStore,
  withdrawalTierUpgradeModalRemoteStateSelector,
} from '@ping/stores/modal.store';
import { isBusinessLoginSelector, useUserInformationStore } from '@ping/stores/userInformation.store';
import {
  Alert,
  AssetSelect,
  Button,
  ComboField,
  Divider,
  NumberField,
  Popup,
  ProgressBar,
  Text,
  Toast,
} from '@ping/uikit';
import { DateTime, format } from '@ping/utils';
import { useNotificationWS } from '@ping/websockets';
import { NotificationEvents, WithdrawNotificationStatus } from '@ping/websockets/useNotificationWS';

import AddressComboBox from '../AddressComboBox';
import MobileConfirmation from '../MobileConfirmation';

import { BusinessWithdrawalConfirmation } from '@ping/components/CryptoTransaction/SendTransaction/BusinessWithdrawalConfirmation';
import { NetworkSelect } from '@ping/components/CryptoTransaction/NetworkSelect';
import { LimitsWarningMessage } from './LimitsWarningMessage';

import { VerifyOwnership } from '@ping/components/CryptoTransaction/SendTransaction/VerifyOwnership';
import { VerifyOwnershipLTC } from '@ping/components/CryptoTransaction/SendTransaction/VerifyOwnershipLTC';
import { modalKeys } from '@ping/constants/modal-keys.constant';
import { useRemoteModal } from '@ping/hooks';
import { ExternalWalletAddressReplace } from '@ping/pages/user/addressbook/components/ModalAddressVew/ExternalWalletAddressReplace';
import Big from 'big.js';
import clsx from 'clsx';
import {
  LinkExternalWalletAddress,
  useWalletVerificationProgress,
  WALLET_VERIFICATION_PENDING_TOAST_ID,
} from '../LinkExternalWalletAddress';
import { ExternalCorePassAddressVerificationAlert } from './ExternalCorePassAddressVerificationAlert';
import { ExternalWalletAddressVerificationAlert } from './ExternalWalletAddressVerificationAlert';
import { ExternalWalletAddressVerificationLTCAlert } from './ExternalWalletAddressVerificationLTCAlert';
import style from './style.module.scss';
import { isGovernanceRequestId } from '@ping/pages/user/business/withdrawal-response.utils';
import { validateAmount } from './validations.helper';

interface ISendTransactionProps {
  assetId?: string;
  onCancel(): void;
  onChangeView(view: MODAL_FULL_VIEW): void;
  onLTCVerify?: (data: VerifyAddressWithSignedFormResult) => void;
}

enum ESteps {
  SEND_TRANSACTION = 'SEND_TRANSACTION',
  SAVE_ADDRESS = 'SAVE_ADDRESS',
  WITHDRAWAL_CONFIRMATION = 'WITHDRAWAL_CONFIRMATION',
}

enum MODAL_FULL_VIEW {
  VERIFY_ADDRESS = 'verifyAddress',
  VERIFY_ADDRESS_LTC = 'verifyAddressLTC',
  QRCODE = 'qrcode',
}

const PER_TRANSACTION_ERROR_CODE = 70101;
const PER_MONTH_ERROR_CODE = 70103;
const PER_YEAR_ERROR_CODE = 70105;
const UPGRADE_TIER_ERROR_CODES = [70100, 70102, 70104];
const UPGRADE_TIER_SUB_ERROR_CODES = [PER_TRANSACTION_ERROR_CODE, PER_MONTH_ERROR_CODE, PER_YEAR_ERROR_CODE];

export function SendTransaction(props: ISendTransactionProps) {
  const isMobile = useMobile();
  const { onChangeView, onLTCVerify } = props;
  const [confirmationLink, setConfirmationLink] = useState<Pick<WalletConnectorQrCodePayload, 'link' | 'expiration'>>({
    link: '',
    expiration: DateTime.minutes(5),
  });
  const [amountError, setAmountError] = useState<string | null>(null);
  const withdrawLimits = useWithdrawalLimits();

  const { data: portfolio } = useGetPortfolio();

  const { data: allAssets } = useGetAllAsset({
    query: {
      staleTime: DateTime.days(1),
      select: ({ data: assets }) => {
        return assets.reduce((map, asset) => map.set(asset.id, asset), new Map());
      },
    },
  });

  const { mutate: sendTransaction } = useWithdrawalWallet();
  const isBusinessLogin = useUserInformationStore(isBusinessLoginSelector);
  const { requestApproval, approval } = useCorePassFinalApproval();
  const [saveStep, setSaveStep] = useState<ESteps>(ESteps.SEND_TRANSACTION);
  const [withdrawConfirmationStep, setWithdrawConfirmationStep] = useState<ESteps>(ESteps.SEND_TRANSACTION);
  const [usersCurrentLimit, setUsersCurrentLimit] = useState(0);

  const withdrawalTierUpgradeModalRemoteState = useModalStore(withdrawalTierUpgradeModalRemoteStateSelector);
  const transferOnProcessingModalState = useModalStore(transferOnProcessingModalRemoteStateSelector);
  const { notificationData, notificationCallBack } = useNotificationWS();
  const targetTierToUpgrade = useRef<number>();
  const exceededLimitAmount = useRef<string>();
  const [isTooltipAddressVisible, toggleTooltipAddressVisible] = useToggle(false);
  const [viewState, setViewState] = useState<WithdrawalAllowanceStatusType>(null);
  const [modalFullView, setModalFullView] = useState<null | MODAL_FULL_VIEW>(null);
  notificationCallBack?.(NotificationEvents.OnWithdrawalStatusChanged);
  const verifyAddressWalletConnector = useVerifyAddressWalletVerificationConnector();
  const addressVerificationFailedModal = useRemoteModal(modalKeys.ADDRESS_VERIFICATION_FAILED);
  const addressVerificationSuccessfulModal = useRemoteModal(modalKeys.ADDRESS_VERIFICATION_SUCCESSFUL);

  const walletVerification = useWalletVerificationProgress({
    onVerified: () => {
      handleCloseWithdrawConfirmationStep();
      Toast.success({ title: t('The address has been successfully saved') });
      setViewState(null);
      setModalFullView(null);
      addressVerificationSuccessfulModal.state.open();
    },
  });

  const { mutate: createWalletSignatureMutate, isLoading: isCreateWalletSignatureLoading } =
    useCreateWalletVerificationRequest();

  useEffect(() => {
    if (notificationData?.status === WithdrawNotificationStatus.SignatureVerified) {
      handleCloseWithdrawConfirmationStep();
      transferOnProcessingModalState.open();
    }
  }, [notificationData]);

  const [userAssetId, setUserAssetId] = React.useState(props.assetId);
  const [userAsset, setUserAsset] = React.useState(undefined);
  const [userAmount, setUserAmount] = React.useState(undefined);
  const [userAddress, setUserAddress] = React.useState(undefined);
  const [userNetwork, setUserNetwork] = React.useState(undefined);
  const [userNetworkConfig, setUserNetworkConfig] = React.useState(undefined);
  const [userTokenId, setUserTokenId] = React.useState(undefined);

  // Initialize userAsset when assetId is provided via props
  useEffect(() => {
    if (props.assetId && allAssets) {
      const asset = allAssets.get(props.assetId);
      if (asset) {
        setUserAsset(asset);
      }
    }
  }, [props.assetId, allAssets]);

  // Get scale from network config (decimals) or fallback to asset scale
  const amountScale = userNetworkConfig?.decimals ?? userAsset?.scale ?? 0;

  // Get portfolio balance for the selected asset
  const selectedAssetBalance = portfolio?.portfolioItems?.find(item => item.assetId === userAssetId);
  const portfolioAvailableAmount = isNil(selectedAssetBalance) ? 0 : selectedAssetBalance.availableAmount;
  const maxAmount = selectedAssetBalance?.availableAmount || 0;

  const availableTokens = useAvailablePortfolioTokens(userAssetId);
  const currencyConversionRate = useConversionAssetToFiat(userAssetId);

  const unusedMonthlyLimitAmount =
    withdrawLimits.limitsList.totalMonthlyLimit - withdrawLimits.limitsList.totalCurrentMonthWithdrawalAmount;
  const limitsListArray = Object.entries(withdrawLimits.limitsList)
    .filter(([key]) => key !== 'totalCurrentMonthWithdrawalAmount')
    .map(([_, value]) => value)
    .concat(unusedMonthlyLimitAmount);

  const convertedTotalAmount = userAmount * currencyConversionRate.rate;
  const amountIsHigherThanLimits = limitsListArray.filter(el => el < convertedTotalAmount);
  const amountIsHigherInt64 = convertedTotalAmount >= Number.MAX_SAFE_INTEGER;
  const exceedingCurrentLimit = convertedTotalAmount >= unusedMonthlyLimitAmount;

  useEffect(() => {
    setUsersCurrentLimit(
      limitsListArray.filter(el => el < convertedTotalAmount).reduce((a, b) => Math.max(a, b), -Infinity)
    );
  }, [withdrawLimits.limitsList, convertedTotalAmount]);

  // Helper function to check if asset is NFT
  const isNftAsset = (assetId: string): boolean => {
    if (!assetId) return false;

    return Boolean(
      userAsset?.commodity_asset?.commodity_type === ApiCommodityType.NonFractionalNFT ||
        userAsset?.commodity_asset?.commodity_type === ApiCommodityType.FractionalNFT
    );
  };

  // Helper function to check if asset is non-fractional NFT
  const isNonFractionalAsset = (assetId: string): boolean => {
    if (!assetId) return false;

    return Boolean(userAsset?.commodity_asset?.commodity_type === ApiCommodityType.NonFractionalNFT);
  };

  const { data: withdrawalReceiveAmountWallet } = useGetWithdrawalReceiveAmountWallet<{
    receiveAmount?: number;
    withdrawalFee?: number;
    withdrawalFeeAssetId?: string;
  }>(
    {
      assetId: userAssetId ?? '',
      paymentSystem: userNetwork ?? '',
      amount: userAmount ?? 0,
      address: userAddress ?? '',
    },
    {
      query: {
        enabled:
          isNotEmpty(userAssetId) && isNotEmpty(userNetwork) && isNotEmpty(userAddress) && Big(userAmount || 0).gt(0),
      },
    }
  );

  useEffect(() => {
    if (isNftAsset(userAssetId) && isNil(userAmount)) {
      setUserAmount(1);
      setAmountError(null);
    }
  }, [userAssetId, userAsset?.commodity_asset?.commodity_type]);

  const handleOnSaveAddress = () => {
    setSaveStep(ESteps.SAVE_ADDRESS);
  };

  const handleOnSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    const send = () =>
      sendTransaction(
        {
          data: {
            assetId: userAssetId,
            address: userAddress,
            amount: userAmount,
            paymentSystem: userNetwork,
            ...(isNonFractionalAsset(userAssetId) &&
              userTokenId && {
                tokenId: userTokenId,
              }),
          },
        },
        {
          onSuccess: (data: string) => {
            gtm.withdrawalEvent(userAssetId);
            setConfirmationLink({ link: data, expiration: DateTime.minutes(5) });
            setWithdrawConfirmationStep(ESteps.WITHDRAWAL_CONFIRMATION);
          },
          onError: (error: any) => {
            const errorData = error?.response?.data;
            if (UPGRADE_TIER_ERROR_CODES.includes(errorData?.errorCode)) {
              const limitExceededData = errorData?.errors?.find(errorItem =>
                UPGRADE_TIER_SUB_ERROR_CODES.includes(+errorItem.key)
              );
              if (limitExceededData) {
                const targetTier = limitExceededData?.data?.SuggestedUserComplianceReviewRequestType?.split('To')[1];
                targetTierToUpgrade.current = targetTier.replace('Tier', '');
                exceededLimitAmount.current = limitExceededData?.data?.WithdrawalLimitAmount;
                withdrawalTierUpgradeModalRemoteState.open();
              }
            }
          },
        }
      );

    if (isBusinessLogin) {
      requestApproval(send);
      return;
    }

    send();
  };

  const handleCloseSaveStep = () => {
    setSaveStep(ESteps.SEND_TRANSACTION);
  };

  const handleCloseWithdrawConfirmationStep = () => {
    setWithdrawConfirmationStep(ESteps.SEND_TRANSACTION);
  };

  const handleSaveAddress = () => {
    handleCloseSaveStep();
  };

  const withdrawalIsAllowedWallet = useIsWithdrawalAllowedWallet(
    {
      address: userAddress,
      paymentSystem: userNetwork,
    },
    {
      query: {
        enabled: isNotEmpty(userAddress) && isNotEmpty(userNetwork),
        cacheTime: DateTime.NEVER,
        staleTime: DateTime.NEVER,
      },
    }
  );

  const withdrawalLiquidityHint = useWithdrawalLiquidityHint(
    userNetwork,
    userAssetId?.toLowerCase() === 'usdc' ? userAssetId : undefined
  );

  const checkWithdrawalIsAllowed = async () => {
    if (isEmpty(userAddress) || isEmpty(userNetwork)) {
      setViewState(null);
      return;
    }

    try {
      const { data } = await withdrawalIsAllowedWallet.refetch();
      setViewState(data.withdrawalAllowanceStatus);
    } catch (error) {
      setViewState(null);
    }
  };
  const checkWithdrawalIsAllowedDebounced = useDebounce(checkWithdrawalIsAllowed, 500);

  if (saveStep === ESteps.SAVE_ADDRESS) {
    const TitleComponent = () => (
      <>
        <ArrowLeft className={style['send-transaction__save-popup__back-arrow']} onClick={handleCloseSaveStep} />
        <span>{t('Save address')}</span>
      </>
    );
    return (
      <Popup title={<TitleComponent />} isOpen onClose={handleCloseSaveStep}>
        <ContactCrypto
          assetId={userAssetId}
          finishCallback={handleSaveAddress}
          defaultValues={{
            address: userAddress,
            network: userNetwork,
          }}
        />
      </Popup>
    );
  }

  if (withdrawConfirmationStep === ESteps.WITHDRAWAL_CONFIRMATION && isGovernanceRequestId(confirmationLink.link)) {
    return (
      <Popup isOpen onClose={handleCloseWithdrawConfirmationStep} fullscreen>
        <BusinessWithdrawalConfirmation requestId={confirmationLink.link.trim()} />
      </Popup>
    );
  }

  if (withdrawConfirmationStep === ESteps.WITHDRAWAL_CONFIRMATION) {
    return (
      <>
        {!isMobile && (
          <Popup isOpen onClose={handleCloseWithdrawConfirmationStep} fullscreen>
            <div className={style['send-transaction__withdrawal-confirmation']}>
              <div className={style['send-transaction__withdrawal-confirmation__block']}>
                <div className={style['send-transaction__withdrawal-confirmation__qr-wrapper']}>
                  <QRCodeSVG size={304} value={decodeURIComponent(confirmationLink.link)} level='L' />
                </div>

                <div className={style['send-transaction__withdrawal-confirmation__info']}>
                  <Text heading='3'>{t('Sign transaction')}</Text>
                  <Text body='regular'>{t('Scan QR code with your CorePass app to verify this transaction.')}</Text>
                </div>
              </div>
            </div>
          </Popup>
        )}

        {isMobile && (
          <Popup isOpen fullscreen>
            <MobileConfirmation
              onClose={handleCloseWithdrawConfirmationStep}
              confirmationLink={confirmationLink.link}
            />
          </Popup>
        )}
      </>
    );
  }

  const handleLTCInstructions = () => {
    setModalFullView(MODAL_FULL_VIEW.VERIFY_ADDRESS_LTC);
    onChangeView(MODAL_FULL_VIEW.VERIFY_ADDRESS_LTC);
  };

  const handleLTCVerifyProcess = () => {
    createWalletSignatureMutate(
      { data: { walletAddress: userAddress, paymentSystem: 'Litecoin' } },
      {
        onSuccess: data => {
          setModalFullView(null);
          onLTCVerify?.(data);
        },
        onError: () => {
          Toast.error({ title: 'Something went wrong, please try again' });
        },
      }
    );
  };

  const handleVerifyViewAddress = () => {
    setModalFullView(MODAL_FULL_VIEW.VERIFY_ADDRESS);
    onChangeView(MODAL_FULL_VIEW.VERIFY_ADDRESS);
  };

  const handleBackToSendTransaction = () => {
    walletVerification.reset();
    setModalFullView(null);
    onChangeView(null);
    setViewState(null);
  };

  const handleVerifyAddress = () => {
    verifyAddressWalletConnector.mutate(
      {
        data: {
          walletAddress: userAddress,
          paymentSystem: userNetwork,
        },
      },
      {
        onSuccess: data => {
          if (data.qrCode) {
            setModalFullView(MODAL_FULL_VIEW.QRCODE);
            onChangeView(MODAL_FULL_VIEW.QRCODE);
            setConfirmationLink(data.qrCode);
            Toast.progress({
              title: t('Waiting for your wallet address'),
              text: t("Please don't close CorePass while the request is being processed"),
              options: { autoClose: false, toastId: WALLET_VERIFICATION_PENDING_TOAST_ID },
            });
            return;
          }
          /* show AddressVerificationFailedModal if possible */
          Toast.error({
            title: 'Complete verification on Corepass to continue',
            text: 'In order to continue go to your CorePass app and complete the proof of ownership verification. Once done, return here to continue.',
            options: { autoClose: 15000 },
          });
        },
        onError: () => {
          addressVerificationFailedModal.state.open();
        },
      }
    );
  };

  if (modalFullView === MODAL_FULL_VIEW.VERIFY_ADDRESS) {
    return (
      <>
        <ArrowLeft
          className={style['send-transaction__save-popup__back-arrow']}
          onClick={handleBackToSendTransaction}
        />
        <VerifyOwnership onSubmit={handleVerifyAddress} isLoading={verifyAddressWalletConnector.isLoading} />
      </>
    );
  }

  if (modalFullView === MODAL_FULL_VIEW.VERIFY_ADDRESS_LTC) {
    return (
      <>
        <ArrowLeft
          className={style['send-transaction__save-popup__back-arrow']}
          onClick={handleBackToSendTransaction}
        />
        <VerifyOwnershipLTC onSubmit={handleLTCVerifyProcess} isLoading={isCreateWalletSignatureLoading} />
      </>
    );
  }

  if (modalFullView === MODAL_FULL_VIEW.QRCODE) {
    return (
      <div>
        <ArrowLeft
          className={clsx(style['send-transaction__save-popup__back-arrow'])}
          onClick={handleBackToSendTransaction}
        />
        <LinkExternalWalletAddress
          link={confirmationLink.link}
          defaultTime={DateTime.seconds(confirmationLink.expiration)}
          isProcessing={walletVerification.isProcessing}
          onFinish={handleVerifyAddress}
        />
      </div>
    );
  }

  return (
    <form
      className={style['send-transaction']}
      onSubmit={handleOnSubmit}
      data-is-assetId-selected={Boolean(userAssetId.length)}
    >
      {approval}
      <WithdrawTierUpgradingStepModal
        limit={exceededLimitAmount.current}
        currentUserTier={withdrawLimits.withdrawalLimit?.complianceTier}
        targetTier={targetTierToUpgrade.current}
      />

      <ProgressBar
        title={t('Monthly limit')}
        currentProgressValue={withdrawLimits.limitsList.totalCurrentMonthWithdrawalAmount}
        maxProgressValue={withdrawLimits.limitsList.totalMonthlyLimit}
        currency={withdrawLimits.selectedCurrency}
        isLoading={withdrawLimits.withdrawalLimitLoading}
        tooltipContent={
          <TooltipWithdrawalLimitsInfo
            perTransactionLimit={withdrawLimits.limitsList.perTransactionLimit}
            totalMonthlyLimit={withdrawLimits.limitsList.totalMonthlyLimit}
            totalYearlyLimit={withdrawLimits.limitsList.totalYearlyLimit}
            userTier={withdrawLimits.withdrawalLimit?.complianceTier}
            limitCurrency={withdrawLimits.selectedCurrency}
          />
        }
      />

      <AssetSelect
        className={style['asset-combo-box__select']}
        name='assetId'
        label={t('Asset')}
        assetId={userAssetId}
        filterFn={isDepositWithdrawAssetListed}
        onChange={(assetId, asset) => {
          setUserAssetId(assetId);
          setUserAsset(asset);
          setUserNetwork(undefined);
          setUserNetworkConfig(undefined);
          setUserAmount(undefined);
          setUserTokenId(undefined);
          setAmountError(null);
        }}
      />

      {/* NFT Security Warning */}
      {isNonFractionalAsset(userAssetId) && (
        <section className={style['nft-security-warning']}>
          <span className={style['nft-security-warning__icon']}>
            <CircleWarningIcon />
          </span>
          <div>
            <Text body='regular'>
              {t(
                'For enhanced security, NFTs are kept in highly protected offline environments. As a result, transfers may take some time, and the requested token will be delivered to the specified address within a maximum of 24 hours.'
              )}
            </Text>
          </div>
        </section>
      )}

      {/* Token Selection for Non-Fractional NFTs */}
      {isNonFractionalAsset(userAssetId) && availableTokens.length > 0 && (
        <PortfolioTokenSelect
          key={userAssetId}
          className={style['send-transaction__token-select']}
          label={t('Token ID')}
          name='tokenId'
          tokens={availableTokens}
          onChange={(amount, tokenId) => {
            setUserAmount(amount);
            setUserTokenId(tokenId);
          }}
        />
      )}

      {/* Message when asset is NonFractionalNFT but user has no tokens */}
      {isNonFractionalAsset(userAssetId) && availableTokens.length === 0 && (
        <div className={style['send-transaction__no-tokens-warning']}>
          <Text body='regular' className={style['send-transaction__warning-text']}>
            {t("This asset requires token selection, but you don't have any tokens available to withdraw.")}
          </Text>
        </div>
      )}

      {/* Network Selection */}
      <section id={style['networks-dropdown']}>
        <NetworkSelect
          label={t('Network')}
          isDisabled={!userAssetId}
          assetId={userAssetId}
          selectedKey={userNetwork}
          onChange={(network, networkConfig) => {
            setUserNetwork(network);
            setUserNetworkConfig(networkConfig);
          }}
          ignore={network => isWithdrawNetworkDisabled(network)}
        />
      </section>

      {/* Amount Field */}

      {!isNftAsset(userAssetId) && (
        <fieldset>
          <legend className={style['send-transaction__amount-label']}>{t('Amount')}</legend>
          <ComboField isDisabled={isNil(userAssetId) || isNil(userNetwork) || !maxAmount}>
            <NumberField
              magnet='end'
              name='assetAmount'
              isDisabled={isNil(userAssetId) || isNil(userNetwork) || !maxAmount}
              minValue='0'
              maxValue={String(maxAmount)}
              value={String(userAmount || '')}
              onChange={amount => {
                const validationError = validateAmount(
                  +amount,
                  userNetworkConfig.min_withdraw,
                  isNil(portfolioAvailableAmount) ? 0 : portfolioAvailableAmount
                );
                setAmountError(validationError);
                setUserAmount(amount);
              }}
              formatOptions={{ maximumFractionDigits: amountScale }}
            />
            {Boolean(maxAmount) && (
              <ComboField.Button
                className={style['send-transaction__max-button']}
                magnet='both'
                isDisabled={isNil(userAssetId) || isEmpty(userAssetId) || isNil(userNetwork) || isEmpty(userNetwork)}
                data-visible={userAmount !== maxAmount}
                onPress={() => setUserAmount(maxAmount)}
              >
                {t('MAX')}
              </ComboField.Button>
            )}
            <ComboField.Divider />
            <ComboField.Box magnet='start'>
              <span>{userAssetId.toUpperCase()}</span>
            </ComboField.Box>
          </ComboField>
          <p className={style['send-transaction__balance']}>
            {t('%s Balance: %s', userAssetId.toUpperCase(), format.crypto(maxAmount, userAssetId))}
          </p>
          {!isNil(amountError) && <p className={style['send-transaction__amount-error']}>{amountError}</p>}
        </fieldset>
      )}

      {withdrawalLiquidityHint.data?.enabled &&
        withdrawalLiquidityHint.data.status !== 'available' &&
        withdrawalLiquidityHint.data.message && (
          <Alert.Root className={style['send-transaction__address-alert']} variant='warning'>
            <Alert.Title>{t('Withdrawal liquidity notice')}</Alert.Title>
            <Alert.Description>{withdrawalLiquidityHint.data.message}</Alert.Description>
            {withdrawalLiquidityHint.data.alternativePaymentSystemMessage && (
              <Alert.Description>{withdrawalLiquidityHint.data.alternativePaymentSystemMessage}</Alert.Description>
            )}
          </Alert.Root>
        )}

      {amountIsHigherThanLimits.length != 0 && !amountIsHigherInt64 && (
        <LimitsWarningMessage
          usersCurrentLimit={usersCurrentLimit}
          perTransactionLimit={withdrawLimits.limitsList.perTransactionLimit}
          totalMonthlyLimit={withdrawLimits.limitsList.totalMonthlyLimit}
          totalYearlyLimit={withdrawLimits.limitsList.totalYearlyLimit}
          userSelectedCurrency={withdrawLimits.selectedCurrency}
          exceedingCurrentLimit={exceedingCurrentLimit}
        />
      )}

      {isNotEmpty(viewState) && <></>}

      <div
        className={style['send-transaction__address']}
        onMouseEnter={() => toggleTooltipAddressVisible(true)}
        onMouseLeave={() => toggleTooltipAddressVisible(false)}
      >
        <AddressComboBox
          assetId={userAssetId}
          network={userNetwork}
          defaultAddress={userAddress}
          isDisabled={!userAssetId || !userAmount || !userNetwork}
          onSave={handleOnSaveAddress}
          onChange={address => {
            setUserAddress(address);
            checkWithdrawalIsAllowedDebounced();
          }}
        />
        {isTooltipAddressVisible && userAddress && (
          <div className={style['send-transaction__address-tooltip']}>{userAddress}</div>
        )}
      </div>

      {!isNil(withdrawalReceiveAmountWallet) && (
        <>
          <Divider />
          <div className={style['send-transaction__info']}>
            <div className={style['send-transaction__info_label']}>
              <span className={style['send-transaction__info_fee-text']}>{t('Transaction fee')}</span>
              <span className={style['send-transaction__info_fee-text']}>
                {`${
                  withdrawalReceiveAmountWallet.withdrawalFee
                } ${withdrawalReceiveAmountWallet.withdrawalFeeAssetId?.toUpperCase()}`}
              </span>
            </div>

            {!isNil(userAssetId) && !isNftAsset(userAssetId) && (
              <div className={style['send-transaction__info_label']}>
                <span className={style['send-transaction__info_label-amount']}>{t('Amount to receive')}</span>
                <span className={style['send-transaction__info_label-amount']}>
                  {format.crypto(withdrawalReceiveAmountWallet.receiveAmount, userAssetId.toUpperCase())}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      <footer className={style['send-transaction__actions']}>
        {viewState === WithdrawalAllowanceStatusType.VerificationRequired && (
          <ExternalWalletAddressVerificationAlert onClick={handleVerifyViewAddress} />
        )}
        {viewState === WithdrawalAllowanceStatusType.VerificationRequiredWithReplace && (
          <ExternalWalletAddressReplace onClick={handleVerifyViewAddress} />
        )}
        {viewState === WithdrawalAllowanceStatusType.ExternalCorePassWalletAddressNotAllowed && (
          <ExternalCorePassAddressVerificationAlert />
        )}
        {viewState === WithdrawalAllowanceStatusType.VerificationRequiredLtc && (
          <ExternalWalletAddressVerificationLTCAlert onClick={handleLTCInstructions} />
        )}
        {!isNil(viewState) &&
          viewState !== WithdrawalAllowanceStatusType.IsAllowed &&
          viewState !== WithdrawalAllowanceStatusType.VerificationRequired &&
          viewState !== WithdrawalAllowanceStatusType.VerificationRequiredWithReplace &&
          viewState !== WithdrawalAllowanceStatusType.ExternalCorePassWalletAddressNotAllowed &&
          viewState !== WithdrawalAllowanceStatusType.VerificationRequiredLtc && (
            <Alert.Root className={style['send-transaction__address-alert']} variant='error'>
              <Alert.Title>{t('Address is not valid for withdrawals')}</Alert.Title>
              <Alert.Description>
                {t('Please select or save a different withdrawal address before continuing.')}
              </Alert.Description>
            </Alert.Root>
          )}
        {(isNil(viewState) || viewState === WithdrawalAllowanceStatusType.IsAllowed) && (
          <>
            <Button
              className={style['send-transaction__button']}
              type='secondary'
              design='general'
              size='large'
              uppercase
              htmlType='button'
              onClick={props.onCancel}
            >
              {t('CANCEL')}
            </Button>

            <Button
              className={style['send-transaction__button']}
              type='primary'
              design='general'
              size='large'
              uppercase
              disabled={
                !userAssetId ||
                (!isNftAsset(userAssetId) && !userAmount) ||
                (isNonFractionalAsset(userAssetId) && (availableTokens.length === 0 || !userTokenId)) ||
                !userAddress ||
                (!isNftAsset(userAssetId) && userAmount > portfolioAvailableAmount) ||
                (amountError !== null && !isEmpty(amountError)) ||
                withdrawalIsAllowedWallet.isLoading
              }
              htmlType='submit'
            >
              {t('Send')}
            </Button>
          </>
        )}
      </footer>
    </form>
  );
}
